import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import CrudPanel from "@/admin/CrudPanel";
import LaporanAdminPanel from "@/admin/LaporanAdminPanel";
import UsersAdminPanel from "@/admin/UsersAdminPanel";
import AuditLogPanel from "@/admin/AuditLogPanel";
import ArtifactsPanel from "@/admin/ArtifactsPanel";
import SiteContentPanel from "@/admin/SiteContentPanel";
import RoleManagementPanel from "@/admin/RoleManagementPanel";
import MaintenancePanel from "@/admin/MaintenancePanel";
import SplashPanel from "@/admin/SplashPanel";
import HospitalInfoPanel from "@/admin/HospitalInfoPanel";
import MediaLibraryPanel from "@/admin/MediaLibraryPanel";
import BackupPanel from "@/admin/BackupPanel";
import ChangePasswordPanel from "@/admin/ChangePasswordPanel";
import LoginHistoryPanel from "@/admin/LoginHistoryPanel";
import SirsSettingsPanel from "@/admin/SirsSettingsPanel";
import ChangelogModal from "@/admin/ChangelogModal";
import DashboardHome from "@/admin/DashboardHome";
import { toast } from "sonner";
import { HOSPITAL } from "@/lib/i18n";
import {
  LogOut,
  Stethoscope,
  UserRound,
  Newspaper,
  Image as ImageIcon,
  Mail,
  Loader2,
  ExternalLink,
  FileBarChart2,
  GalleryHorizontalEnd,
  BarChart3,
  Info,
  Sparkles,
  ShieldCheck,
  ScrollText,
  Package,
  FileJson,
  KeyRound,
  Wrench,
  Megaphone,
  Building2,
  FolderOpen,
  DatabaseBackup,
  Bell,
  LayoutDashboard,
  Trash2,
  Reply,
  MonitorSmartphone,
  Server,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Beranda", icon: LayoutDashboard, key: "dashboard", always: true, end: true },
  { to: "/admin/hero-slides", label: "Hero Slides", icon: GalleryHorizontalEnd, key: "hero-slides" },
  { to: "/admin/hero-stats", label: "Hero Stats", icon: BarChart3, key: "hero-stats" },
  { to: "/admin/about-content", label: "Tentang Kami — Konten", icon: Info, key: "about-content" },
  { to: "/admin/about-pillars", label: "Tentang Kami — Pilar", icon: Sparkles, key: "about-pillars" },
  { to: "/admin/services", label: "Layanan", icon: Stethoscope, key: "services" },
  { to: "/admin/doctors", label: "Dokter", icon: UserRound, key: "doctors" },
  { to: "/admin/news", label: "Berita", icon: Newspaper, key: "news" },
  { to: "/admin/gallery", label: "Galeri", icon: ImageIcon, key: "gallery" },
  { to: "/admin/media", label: "Media Library", icon: FolderOpen, key: "media" },
  { to: "/admin/laporan", label: "Laporan", icon: FileBarChart2, key: "laporan" },
  { to: "/admin/site-content", label: "Konten Profil", icon: FileJson, key: "site-content" },
  { to: "/admin/hospital-info", label: "Info Rumah Sakit", icon: Building2, key: "hospital-info" },
  { to: "/admin/splash", label: "Splash Pengumuman", icon: Megaphone, key: "splash" },
  { to: "/admin/contacts", label: "Pesan Kontak", icon: Mail, key: "contacts" },
  { to: "/admin/users", label: "User Admin", icon: ShieldCheck, key: "users", superadminOnly: true },
  { to: "/admin/audit-logs", label: "Audit Log", icon: ScrollText, key: "audit-logs", superadminOnly: true },
  { to: "/admin/artifacts", label: "Download Artifacts", icon: Package, key: "artifacts", superadminOnly: true },
  { to: "/admin/role-management", label: "Manajemen Role", icon: KeyRound, key: "role-management", superadminOnly: true },
  { to: "/admin/maintenance", label: "Maintenance Mode", icon: Wrench, key: "maintenance", superadminOnly: true },
  { to: "/admin/backup", label: "Backup & Restore", icon: DatabaseBackup, key: "backup", superadminOnly: true },
  { to: "/admin/sirs-settings", label: "Mode SIRS API", icon: Server, key: "sirs-settings", superadminOnly: true },
  { to: "/admin/change-password", label: "Ganti Password", icon: KeyRound, key: "change-password", always: true },
  { to: "/admin/login-history", label: "Riwayat Login", icon: MonitorSmartphone, key: "login-history", always: true },
];

const ContactsView = ({ onRead }) => {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    authAxios
      .get("/admin/contacts")
      .then((r) => {
        if (!active) return;
        setItems(r.data);
        const hasUnread = (r.data || []).some((m) => m.read === false);
        if (hasUnread) {
          authAxios
            .post("/admin/contacts/mark-read", { all: true })
            .then(() => onRead && onRead())
            .catch(() => {});
        } else if (onRead) {
          onRead();
        }
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [authAxios, onRead]);

  const deleteContact = async (id) => {
    if (!window.confirm("Hapus pesan ini? Tindakan ini tidak bisa dibatalkan.")) return;
    try {
      await authAxios.delete(`/admin/contacts/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success("Pesan dihapus");
    } catch (e) {
      toast.error("Gagal menghapus pesan");
    }
  };

  const replyHref = (m) => {
    const subject = encodeURIComponent(`Re: ${m.subject || ""}`);
    const body = encodeURIComponent(
      `\n\n---\nMenanggapi pesan Anda:\n"${m.message || ""}"\n\nHormat kami,\nRS Bhayangkara Tarakan`
    );
    return `mailto:${m.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div data-testid="admin-panel-contacts">
      <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
        Pesan Kontak Masuk
      </h2>
      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">
          Belum ada pesan masuk.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-contact-${m.id}`}
              className={`bg-white rounded-2xl border p-5 ${
                m.read === false
                  ? "border-rose-200 ring-1 ring-rose-100"
                  : "border-slate-100"
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                    {m.read === false && (
                      <span className="inline-block w-2 h-2 rounded-full bg-rose-500" title="Belum dibaca" />
                    )}
                    {m.subject}
                    {m.read === false && (
                      <span className="text-[10px] uppercase tracking-wide font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">
                        Baru
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {m.name} • {m.email}
                    {m.phone && ` • ${m.phone}`}
                  </div>
                </div>
                <div className="text-xs text-slate-400">
                  {new Date(m.created_at).toLocaleString("id-ID")}
                </div>
              </div>
              <div className="text-sm text-slate-600 whitespace-pre-wrap mt-3">
                {m.message}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <a
                  href={replyHref(m)}
                  data-testid={`contact-reply-${m.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-800 hover:bg-blue-900 text-white text-xs font-medium"
                >
                  <Reply className="h-3.5 w-3.5" />
                  Balas Email
                </a>
                <button
                  onClick={() => deleteContact(m.id)}
                  data-testid={`contact-delete-${m.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Small SIRS connection indicator for the admin header. Only renders when
// "Mode SIRS API" is ON. Green = connected, red (pulsing) = disrupted.
const SirsStatusIndicator = () => {
  const { authAxios, user } = useAuth();
  const [st, setSt] = useState(null);
  const isSuperadmin = user && user.role === "superadmin";

  useEffect(() => {
    if (!isSuperadmin) return;
    let alive = true;
    const load = () =>
      authAxios
        .get("/sirs/status")
        .then((r) => {
          if (alive) setSt(r.data);
        })
        .catch(() => {
          if (alive) setSt(null);
        });
    load();
    const id = setInterval(load, 45000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [authAxios, isSuperadmin]);

  // "Mode SIRS API" status is superadmin-only.
  if (!isSuperadmin || !st || !st.mode_enabled) return null;
  const ok = st.connected;

  const inner = (
    <>
      <span className="relative flex h-2 w-2">
        {!ok && (
          <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${
            ok ? "bg-emerald-500" : "bg-rose-500"
          }`}
        />
      </span>
      SIRS {ok ? "Online" : "Gangguan"}
    </>
  );

  const cls = `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
    ok
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-rose-50 text-rose-700 border-rose-200"
  }`;

  return (
    <NavLink
      to="/admin/sirs-settings"
      data-testid="sirs-status-indicator"
      title={ok ? "SIRS tersambung — klik untuk detail" : "SIRS terputus — klik untuk detail"}
      className={`${cls} hover:brightness-95`}
    >
      {inner}
    </NavLink>
  );
};


const AdminDashboard = () => {
  const { user, logout, authAxios } = useAuth();
  const nav = useNavigate();
  const [adminMenus, setAdminMenus] = useState(null); // null = loading, Set = loaded
  const [appVersion, setAppVersion] = useState("");
  const [changelog, setChangelog] = useState([]);
  const [showChangelog, setShowChangelog] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [seenVersion, setSeenVersion] = useState("");
  const [contactsUnread, setContactsUnread] = useState(0);

  const SEEN_VERSION_KEY = "rsb_seen_version";

  useEffect(() => {
    authAxios
      .get("/admin/contacts/unread-count")
      .then((r) => setContactsUnread(r.data?.unread || 0))
      .catch(() => {});
  }, [authAxios]);

  useEffect(() => {
    authAxios
      .get("/changelog")
      .then((r) => {
        const current = r.data?.current || "";
        setAppVersion(current);
        setChangelog(r.data?.items || []);
        if (current) {
          let seen = "";
          try {
            seen = localStorage.getItem(SEEN_VERSION_KEY) || "";
          } catch (e) {
            /* ignore */
          }
          setSeenVersion(seen);
          // Show "what's new" only when the user has seen a previous version.
          // First-time users get the version silently marked as seen.
          if (!seen) {
            try {
              localStorage.setItem(SEEN_VERSION_KEY, current);
            } catch (e) {
              /* ignore */
            }
            setSeenVersion(current);
          } else if (seen !== current) {
            setWhatsNewOpen(true);
          }
        }
      })
      .catch(() => {});
  }, [authAxios]);

  const markVersionSeen = () => {
    try {
      if (appVersion) localStorage.setItem(SEEN_VERSION_KEY, appVersion);
    } catch (e) {
      /* ignore */
    }
    setSeenVersion(appVersion);
  };

  const acknowledgeWhatsNew = () => {
    setWhatsNewOpen(false);
    markVersionSeen();
  };

  const openChangelog = () => {
    setWhatsNewOpen(false);
    setShowChangelog(true);
    markVersionSeen();
  };

  const hasUnreadVersion = !!appVersion && seenVersion !== appVersion;

  // Fetch role permissions for non-superadmin users
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if (user.role === "siperkasa") return; // operators have no admin menus
    if (user.role === "superadmin") {
      // Superadmin sees everything; no fetch needed
      setAdminMenus("ALL");
      return;
    }
    authAxios
      .get("/admin/role-permissions")
      .then((r) => {
        const list = (r.data && r.data.permissions && r.data.permissions.admin) || [];
        setAdminMenus(new Set(list));
      })
      .catch(() => {
        // Fallback: show nothing if we cannot determine permissions
        setAdminMenus(new Set());
      });
  }, [user, authAxios]);

  // Loading state: user is still being checked (null or undefined before context resolves)
  if (user === null || user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-blue-800" />
      </div>
    );
  }
  // Not authenticated: explicit false, or any non-object payload
  if (user === false || typeof user !== "object") {
    return <Navigate to="/admin/login" replace />;
  }
  // SIPERKASA operators have no admin-panel access → send them to SIPERKASA.
  if (user.role === "siperkasa") {
    return <Navigate to="/siperkasa" replace />;
  }

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  const isSuperadmin = user && user.role === "superadmin";

  // Filter NAV based on role permissions
  const visibleNav = NAV.filter((n) => {
    if (n.always) return true;
    if (n.superadminOnly) return isSuperadmin;
    if (isSuperadmin) return true;
    // Admin role: check dynamic permissions
    if (adminMenus === "ALL") return true;
    if (adminMenus === null) return false; // still loading
    return adminMenus.has(n.key);
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-slate-100 p-6 hidden lg:flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 mb-8 shrink-0">
          <img src={HOSPITAL.logo} alt="Logo" className="h-10 w-10 rounded-lg object-cover" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-slate-900 text-sm">Admin Panel</div>
            <div className="text-xs text-slate-500">RS Bhayangkara</div>
          </div>
          <button
            onClick={openChangelog}
            data-testid="admin-notif-bell"
            title={
              hasUnreadVersion
                ? "Ada catatan versi baru"
                : "Catatan versi"
            }
            className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition"
          >
            <Bell className="h-5 w-5" />
            {hasUnreadVersion && (
              <span
                data-testid="admin-notif-dot"
                className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse"
              />
            )}
          </button>
        </div>

        <div className="mb-6 shrink-0 -mt-4">
          <SirsStatusIndicator />
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1 -mr-1">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive
                    ? "bg-blue-800 text-white shadow-md shadow-blue-900/20"
                    : "text-slate-600 hover:bg-blue-50 hover:text-blue-800"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span className="flex-1">{n.label}</span>
              {n.key === "contacts" && contactsUnread > 0 && (
                <span
                  data-testid="nav-contacts-badge"
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center"
                >
                  {contactsUnread}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="pt-4 mt-3 border-t border-slate-100 space-y-2 shrink-0">
          <a
            href="/"
            data-testid="admin-view-site"
            className="flex items-center gap-2 text-xs text-slate-500 hover:text-blue-800 px-4 py-2"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Lihat Website
          </a>
          <div className="px-4 py-2">
            <div className="text-xs text-slate-400">Login sebagai</div>
            <div className="text-sm font-medium text-slate-900 truncate">
              {user?.email || "—"}
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-red-700 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Keluar
          </button>
          {appVersion && (
            <button
              onClick={openChangelog}
              className="text-[11px] text-slate-400 hover:text-indigo-600 text-center pt-2 w-full transition-colors"
              data-testid="admin-app-version"
              title="Lihat catatan versi"
            >
              Versi aplikasi v{appVersion}
            </button>
          )}
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden bg-white border-b border-slate-100 p-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <img src={HOSPITAL.logo} alt="Logo" className="h-8 w-8 rounded-lg" />
          <div className="font-heading font-bold text-slate-900 text-sm">Admin Panel</div>
        </div>
        <div className="flex items-center gap-3">
          <SirsStatusIndicator />
          <button
            onClick={openChangelog}
            data-testid="admin-notif-bell-mobile"
            className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100"
            title="Catatan versi"
          >
            <Bell className="h-5 w-5" />
            {hasUnreadVersion && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />
            )}
          </button>
          <button onClick={doLogout} className="text-xs text-red-700 font-medium">
            Logout
          </button>
        </div>
      </div>

      {/* Mobile nav pills */}
      <div className="lg:hidden overflow-x-auto bg-white border-b border-slate-100 px-4 py-2 flex gap-2 sticky top-[57px] z-30">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                isActive ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-600"
              }`
            }
          >
            <n.icon className="h-3.5 w-3.5" />
            {n.label}
            {n.key === "contacts" && contactsUnread > 0 && (
              <span className="ml-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                {contactsUnread}
              </span>
            )}
          </NavLink>
        ))}
      </div>

      {/* Content */}
      <main className="lg:pl-64 p-4 md:p-8">
        <Routes>
          <Route index element={<DashboardHome />} />
          <Route path="services" element={<CrudPanel entity="services" />} />
          <Route path="hero-slides" element={<CrudPanel entity="hero_slides" />} />
          <Route path="hero-stats" element={<CrudPanel entity="hero_stats" />} />
          <Route path="about-content" element={<CrudPanel entity="about_content" />} />
          <Route path="about-pillars" element={<CrudPanel entity="about_pillars" />} />
          <Route path="doctors" element={<CrudPanel entity="doctors" />} />
          <Route path="news" element={<CrudPanel entity="news" />} />
          <Route path="gallery" element={<CrudPanel entity="gallery" />} />
          <Route path="media" element={<MediaLibraryPanel />} />
          <Route path="laporan" element={<LaporanAdminPanel />} />
          <Route path="site-content" element={<SiteContentPanel />} />
          <Route path="hospital-info" element={<HospitalInfoPanel />} />
          <Route path="splash" element={<SplashPanel />} />
          <Route path="contacts" element={<ContactsView onRead={() => setContactsUnread(0)} />} />
          <Route path="users" element={<UsersAdminPanel />} />
          <Route path="audit-logs" element={<AuditLogPanel />} />
          <Route path="artifacts" element={<ArtifactsPanel />} />
          <Route path="role-management" element={<RoleManagementPanel />} />
          <Route path="maintenance" element={<MaintenancePanel />} />
          <Route path="backup" element={<BackupPanel />} />
          <Route path="sirs-settings" element={<SirsSettingsPanel />} />
          <Route path="change-password" element={<ChangePasswordPanel />} />
          <Route path="login-history" element={<LoginHistoryPanel />} />
        </Routes>
      </main>

      {/* "What's new" popup — auto-shown when version increased */}
      <ChangelogModal
        open={whatsNewOpen}
        onClose={acknowledgeWhatsNew}
        current={appVersion}
        items={changelog}
        isWhatsNew
      />
      {/* Full changelog — opened from the sidebar version */}
      <ChangelogModal
        open={showChangelog}
        onClose={() => setShowChangelog(false)}
        current={appVersion}
        items={changelog}
      />
    </div>
  );
};

export default AdminDashboard;
